import { useEffect, useState, useCallback } from 'react'
import { PageHeader, DataTable, StatusBadge, btnPrimary, btnSecondary, btnDanger, colors } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { UdmRolloutModal } from './UdmRolloutModal'
import { UdmPackageUploadModal } from './UdmPackageUploadModal'

interface RolloutSite {
  readonly id: string
  readonly name: string
}

interface RolloutRow {
  readonly id: string
  readonly version: string
  readonly status: string
  readonly mandatory: boolean
  readonly notes: string | null
  readonly createdAt: string
  readonly updatedAt: string | null
  readonly createdBy: string
  readonly sites: readonly RolloutSite[]
}

const columns: readonly Column<RolloutRow>[] = [
  {
    key: 'version',
    header: 'Version',
    render: (r) => <strong style={{ fontFamily: 'monospace' }}>{r.version}</strong>,
    sortable: true,
  },
  {
    key: 'status',
    header: 'Status',
    render: (r) => <StatusBadge status={r.status} label={r.status} />,
  },
  {
    key: 'sites',
    header: 'Targeted sites',
    render: (r) => (r.status === 'general' ? 'All' : String(r.sites.length)),
  },
  {
    key: 'mandatory',
    header: 'Mandatory',
    render: (r) => (r.mandatory ? 'Yes' : 'No'),
  },
  {
    key: 'createdAt',
    header: 'Created',
    render: (r) => new Date(r.createdAt).toLocaleDateString(),
    sortable: true,
  },
]

export function UdmRolloutsPage() {
  const [rows, setRows] = useState<readonly RolloutRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<RolloutRow | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const reload = useCallback(() => setRefreshKey((k) => k + 1), [])

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly RolloutRow[]>('/admin/udm/rollouts')
      .then((res) => {
        if (cancelled) return
        setRows(res.data ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load rollouts')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [refreshKey])

  const handlePromote = async (id: string) => {
    setActionError(null)
    try {
      await api.patch(`/admin/udm/rollouts/${id}`, { status: 'general' })
      reload()
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Promote failed')
    }
  }

  const handlePause = async (id: string) => {
    setActionError(null)
    try {
      await api.patch(`/admin/udm/rollouts/${id}`, { status: 'paused' })
      reload()
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Pause failed')
    }
  }

  const handleDelete = async (id: string) => {
    setActionError(null)
    try {
      await api.delete(`/admin/udm/rollouts/${id}`)
      reload()
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Delete failed')
    }
  }

  const openCreate = () => {
    setEditingRow(null)
    setModalOpen(true)
  }

  const openEdit = (row: RolloutRow) => {
    setEditingRow(row)
    setModalOpen(true)
  }

  const actionColumns: readonly Column<RolloutRow>[] = [
    ...columns,
    {
      key: '_actions',
      header: 'Actions',
      render: (r) => (
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {r.status !== 'general' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                void handlePromote(r.id)
              }}
              style={{ ...btnPrimary, fontSize: '12px', padding: '0.2rem 0.5rem' }}
            >
              Promote
            </button>
          )}
          {r.status !== 'paused' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                void handlePause(r.id)
              }}
              style={{ ...btnSecondary, fontSize: '12px', padding: '0.2rem 0.5rem' }}
            >
              Pause
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              openEdit(r)
            }}
            style={{ ...btnSecondary, fontSize: '12px', padding: '0.2rem 0.5rem' }}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              void handleDelete(r.id)
            }}
            style={{ ...btnDanger, fontSize: '12px', padding: '0.2rem 0.5rem' }}
          >
            Delete
          </button>
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="UDM Rollouts"
        subtitle="Manage UDM agent version rollouts per site"
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={() => setUploadOpen(true)} style={btnSecondary}>
              Upload Package
            </button>
            <button type="button" onClick={openCreate} style={btnPrimary}>
              New Rollout
            </button>
          </div>
        }
      />

      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {actionError && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {actionError}
        </div>
      )}

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={actionColumns}
          rows={rows}
          keyField="id"
          emptyMessage="No rollouts configured yet. Click New Rollout to create one."
        />
      )}

      <UdmRolloutModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setEditingRow(null)
        }}
        editingRow={
          editingRow
            ? {
                id: editingRow.id,
                version: editingRow.version,
                status: editingRow.status as 'canary' | 'general' | 'paused',
                mandatory: editingRow.mandatory,
                notes: editingRow.notes ?? '',
                siteIds: editingRow.sites.map((s) => s.id),
              }
            : null
        }
        onSaved={() => {
          setModalOpen(false)
          setEditingRow(null)
          reload()
        }}
      />

      <UdmPackageUploadModal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={reload} />
    </div>
  )
}
